import { Search } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListSubheader,
  TextField,
  Typography,
} from '@mui/material';
import { FormControlLabel } from '@mui/material';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import { useI18n } from '../../../../../../application/language/useI18n';
import { PasswordField } from '../../../design/form/PasswordField';
import { ContentExportDomain } from './store/ContentExportDomain';
import { IContentExportDomain } from './store/IContentExportDomain';

export const ContentExportPage = observer(() => {
  const [exportDomain] = useState<IContentExportDomain>(new ContentExportDomain());
  const i18n = useI18n();

  useEffect(() => {
    exportDomain.boot();
  }, [exportDomain]);

  if (exportDomain.ui.isLoading.entity) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        maxHeight: '93vh',
        height: '100%',
        p: 2,
        boxSizing: 'border-box',
      }}
    >
      <List
        sx={{
          width: '100%',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          padding: '0px 16px 16px 16px',
        }}
        subheader={
          <ListSubheader
            component="div"
            sx={{
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0px 0px 8px 0px',
              position: 'sticky',
              top: 0,
              zIndex: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    edge="start"
                    checked={exportDomain.ui.isSelectedAll.entity}
                    onChange={(e) => exportDomain.toggleSelecteAll(e.target.checked)}
                  />
                }
                label={<Typography variant="h6">{i18n.translate('contentTransfer.exportPage.toggle')}</Typography>}
                sx={{ m: 0, cursor: 'pointer' }}
              />

              <TextField
                size="small"
                onChange={(event) => {
                  exportDomain.handleSearchByGroupsAndName(event.target.value);
                }}
                placeholder={i18n.translate('contentTransfer.exportPage.search')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton edge="end">
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {exportDomain.ui.unitsForExporting.list.length} / {exportDomain.ui.units.list.length}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                disabled={exportDomain.ui.unitsForExporting.list.length === 0 || exportDomain.ui.isExporting.entity}
                onClick={() => exportDomain.openPasswordDialog()}
              >
                {exportDomain.ui.isExporting.entity
                  ? `${i18n.translate('contentTransfer.exportPage.state')} ${
                      exportDomain.ui.exportProgress.entity.current
                    }/${exportDomain.ui.exportProgress.entity.total}`
                  : i18n.translate('contentTransfer.exportPage.button')}
              </Button>

              <Dialog
                fullWidth
                open={exportDomain.ui.isPasswordDialogOpen.entity}
                onClose={() => exportDomain.closePasswordDialog()}
              >
                <DialogTitle>{i18n.translate('contentTransfer.exportPage.modalTitle')}</DialogTitle>
                <DialogContent>
                  <PasswordField
                    label={i18n.translate('contentTransfer.exportPage.inputPlaceholder')}
                    value={exportDomain.ui.exportPassword.entity}
                    onChange={exportDomain.handlePasswordChange}
                    helperText={i18n.translate('contentTransfer.exportPage.passwordHint')}
                    onEnter={exportDomain.confirmPasswordAndExport}
                    name="export-secret"
                    errorMessage={exportDomain.ui.validationErrors.list}
                    autoFocus
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={() => exportDomain.closePasswordDialog()}>
                    {i18n.translate('contentTransfer.exportPage.decline')}
                  </Button>
                  <Button
                    variant="contained"
                    disabled={!exportDomain.ui.exportPassword.entity}
                    onClick={() => exportDomain.confirmPasswordAndExport()}
                  >
                    {i18n.translate('contentTransfer.exportPage.confirm')}
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </ListSubheader>
        }
      >
        {exportDomain.ui.filteredUnits.list.map((unit) => {
          const isChecked = exportDomain.ui.unitsForExporting.list.some((u) => u.unitId === unit.id);
          return (
            <ListItem key={unit.id} sx={{ pl: 0 }}>
              <FormControlLabel
                sx={{ width: '100%', cursor: 'pointer' }}
                control={
                  <Checkbox
                    checked={isChecked}
                    onChange={(e) => exportDomain.toggleSelectUnit(unit.id, e.target.checked)}
                  />
                }
                label={unit.settings?.titleHeading ?? unit.id}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
});
